import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Target, Brain, ArrowRight, Star, AlertTriangle,
  FileText, PenTool, Youtube,
  ChevronDown, X,
  MessageCircle, Sparkles
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'S1-1'

// ── 스토리텔링 인트로 데이터 ─────────────────────────────────────────────────
interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  SA01: {
    scene: '📋 학교 설문 조사. 학생들의 하루 공부 시간 자료를 정리하는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '선생님, 이 자료를 어떻게 정리해야 할지 모르겠어요. 숫자가 너무 많아요.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '자료를 정리할 때는 먼저 변수의 종류를 파악해야 해요. 수량으로 나타낼 수 있는 자료를 양적 변수, 범주로 나타내는 자료를 질적 변수라고 해요.' },
      { speaker: 'friend', emoji: '🤔', name: '수아', text: '공부 시간은 숫자니까 양적 변수고, 좋아하는 과목은 질적 변수겠네요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '정확해요! 양적 변수는 연속형(키, 몸무게)과 이산형(학생 수, 자녀 수)으로 나뉩니다. 자료 정리의 첫 걸음은 변수의 종류를 아는 거예요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '양적 변수(수량) vs 질적 변수(범주) | 연속형 vs 이산형' },
    ],
    reveal: '양적 변수: 키·몸무게·시간 | 질적 변수: 성별·혈액형·과목',
    revealEmoji: '📋',
  },
  SA02: {
    scene: '🌱 줄기와 잎 그림. 반 학생 20명의 수학 점수를 한눈에 보는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '점수를 줄기잎 그림으로 나타내면 뭐가 좋아요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '줄기잎 그림은 원래 데이터를 그대로 보존하면서 분포를 한눈에 볼 수 있어요! 십의 자리를 줄기, 일의 자리를 잎으로 나타내면 돼요.' },
      { speaker: 'friend', emoji: '😄', name: '수아', text: '78점이면 줄기=7, 잎=8이 되는 거죠? 그럼 75, 78, 79는 줄기 7에 잎 5, 8, 9!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '완벽해요! 잎은 반드시 오름차순으로 정렬해야 해요. 그러면 최솟값, 최댓값, 중앙값을 한눈에 파악할 수 있답니다.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '줄기=십의 자리, 잎=일의 자리 | 잎은 오름차순 정렬' },
    ],
    reveal: '줄기잎 그림: 원자료 보존 + 분포 시각화 | 잎은 오름차순',
    revealEmoji: '🌱',
  },
  SA03: {
    scene: '📊 도수분포표. 50명 학생의 통학 시간을 구간별로 정리하는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '자료가 너무 많아서 줄기잎 그림으로는 힘들어요. 다른 방법이 있나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '도수분포표를 쓰면 돼요! 자료를 일정한 간격(계급)으로 나누고, 각 계급에 속하는 자료의 수(도수)를 세면 됩니다.' },
      { speaker: 'friend', emoji: '🤔', name: '수아', text: '계급의 크기는 어떻게 정해요? 다 같아야 하나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '계급의 크기는 모두 같아야 해요! 계급값은 그 계급의 중간값이에요. 예를 들어 10분 이상 20분 미만의 계급값은 15분입니다.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '계급=구간, 도수=그 구간의 자료 수, 계급값=계급 중간값' },
    ],
    reveal: '계급 크기 일정 | 도수=각 계급의 자료 수 | 계급값=중간값',
    revealEmoji: '📊',
  },
  SA04: {
    scene: '📈 히스토그램. 도수분포표를 막대그래프로 나타내는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '히스토그램이 일반 막대그래프랑 뭐가 달라요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '히스토그램은 막대 사이에 간격이 없어요! 연속적인 양적 자료를 나타내기 때문이죠. 가로축은 계급, 세로축은 도수입니다.' },
      { speaker: 'friend', emoji: '😮', name: '수아', text: '도수분포다각형은 히스토그램 각 막대의 꼭대기 중간점을 선으로 연결한 거죠?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 양 끝은 도수가 0인 가상의 계급까지 연결해요. 두 집단의 분포를 비교할 때 도수분포다각형이 편리합니다.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '히스토그램: 막대 간격 없음 | 도수분포다각형: 꼭대기 연결' },
    ],
    reveal: '히스토그램: 막대 간격 없음 | 다각형: 꼭대기 중간점 연결',
    revealEmoji: '📈',
  },
  SA05: {
    scene: '⚖️ 상대도수. 두 반의 성적 분포를 공정하게 비교하는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '1반은 30명, 2반은 40명인데 성적을 어떻게 비교해요? 학생 수가 달라서 도수로는 비교가 안 되잖아요.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '바로 그래서 상대도수를 쓰는 거예요! 상대도수 = 그 계급의 도수 ÷ 전체 도수수. 전체를 1로 봤을 때의 비율이에요.' },
      { speaker: 'friend', emoji: '😄', name: '수아', text: '1반에서 90점 이상인 학생이 6명이면 상대도수는 6÷30=0.2, 즉 20%네요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '완벽해요! 누적도수는 처음 계급부터 그 계급까지의 도수를 모두 더한 거예요. 누적상대도수는 1에 가까울수록 전체의 더 많은 비율을 포함하게 됩니다.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '상대도수 = 그 계급의 도수 ÷ 전체 도수 | 누적도수 = 처음부터 합계' },
    ],
    reveal: '상대도수 = 도수÷전체도수 | 전체 상대도수의 합=1',
    revealEmoji: '⚖️',
  },
}

// ── 학습 단계 정의 ───────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'SA01',
    title: '변수와 자료의 종류',
    subtitle: '양적 변수와 질적 변수, 연속형과 이산형을 구분한다',
    icon: '📋',
    color: 'bg-violet-500',
    lightColor: 'bg-violet-50',
    borderColor: 'border-violet-300',
    textColor: 'text-violet-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['변수', '양적변수', '질적변수', '연속형', '이산형'],
    objective: '자료의 변수 종류를 양적/질적으로 구분하고, 양적 변수를 연속형과 이산형으로 분류할 수 있다.',
    concept: `**변수와 자료의 종류**

📌 변수(Variable)란?
• 조사 대상에서 측정하거나 관찰하는 특성
• 사람마다, 대상마다 값이 다를 수 있음

📌 양적 변수 (수량으로 나타낼 수 있는 변수)
• 키, 몸무게, 점수, 시간, 나이 등
• 수학적 연산(덧셈·뺄셈·평균)이 의미 있음

  ① 연속형: 측정값 사이에 어떤 값도 가능
     → 키, 몸무게, 온도, 시간
  ② 이산형: 셀 수 있는 정수값만 가능
     → 학생 수, 자녀 수, 결석 횟수

📌 질적 변수 (범주로 나타내는 변수)
• 성별, 혈액형, 좋아하는 과목, 지역 등
• 숫자로 바꿔도 크기 비교나 연산이 무의미

📌 정리표
| 구분 | 예시 | 특징 |
|------|------|------|
| 양적-연속 | 키·체온·시간 | 소수점 가능 |
| 양적-이산 | 학생 수·횟수 | 정수만 |
| 질적 | 성별·혈액형 | 범주 분류 |

⚠️ 주의
• 학번·전화번호처럼 숫자이지만 계산이 무의미하면 질적 변수
• 통계 방법은 변수의 종류에 따라 달라짐`,
    keyPoints: [
      '양적 변수: 수량으로 나타낼 수 있음 (키, 점수, 시간)',
      '질적 변수: 범주로 나타냄 (성별, 혈액형, 과목)',
      '연속형: 소수 가능 (키, 온도) / 이산형: 정수만 (학생 수)',
      '학번·우편번호는 숫자지만 질적 변수',
    ],
    examples: [
      {
        question: '다음 변수를 양적(연속/이산)과 질적으로 분류하시오.\n① 반 학생 수  ② 혈액형  ③ 몸무게  ④ 좋아하는 색',
        answer: '① 양적-이산  ② 질적  ③ 양적-연속  ④ 질적',
        explanation: '① 학생 수는 셀 수 있는 정수 → 양적-이산\n② 혈액형은 A·B·O·AB 범주 → 질적\n③ 몸무게는 소수 가능 → 양적-연속\n④ 색은 범주 → 질적',
      },
      {
        question: '전교생 500명의 수학 점수를 조사하려 한다.\n이 자료의 변수 종류와 적절한 정리 방법은?',
        answer: '양적-연속 변수, 도수분포표나 히스토그램으로 정리',
        explanation: '점수는 수량으로 나타내고 소수 가능 → 양적-연속\n자료가 많을 때는 도수분포표·히스토그램이 적절',
      },
    ],
    practice: {
      question: '다음 중 질적 변수를 모두 고르시오.\n① 체온(℃)  ② 직업의 종류  ③ 일주일 운동 횟수\n④ 출신 지역  ⑤ 턱걸이 개수  ⑥ 선호하는 음악 장르',
      answer: '② 직업의 종류, ④ 출신 지역, ⑥ 선호하는 음악 장르',
      hint: '숫자로 나타낼 수 없거나, 나타내도 크기 비교가 무의미한 것이 질적 변수!',
      steps: [
        '① 체온: 소수 가능한 수량 → 양적-연속',
        '② 직업의 종류: 범주 분류 → 질적 ✓',
        '③ 운동 횟수: 셀 수 있는 정수 → 양적-이산',
        '④ 출신 지역: 범주 분류 → 질적 ✓',
        '⑤ 턱걸이 개수: 셀 수 있는 정수 → 양적-이산',
        '⑥ 음악 장르: 범주 분류 → 질적 ✓',
      ],
    },
    misconceptions: ['ST01'],
  },
  {
    id: 'SA02',
    title: '줄기잎 그림',
    subtitle: '줄기잎 그림으로 자료의 분포를 시각적으로 파악한다',
    icon: '🌱',
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
    textColor: 'text-emerald-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['줄기잎그림', '줄기', '잎', '중앙값', '분포'],
    objective: '줄기잎 그림을 그리고, 이를 이용하여 최솟값·최댓값·중앙값을 찾을 수 있다.',
    concept: `**줄기잎 그림 (Stem-and-Leaf Plot)**

📌 줄기잎 그림이란?
• 원자료(raw data)를 그대로 보존하면서 분포를 시각화
• 줄기: 십의 자리(또는 백의 자리)
• 잎: 일의 자리
• 잎은 반드시 오름차순으로 정렬!

📌 그리는 방법
① 자료의 범위 파악 → 줄기 결정
② 세로로 줄기 나열
③ 각 자료의 잎을 해당 줄기 옆에 기록
④ 잎을 오름차순으로 정렬

📌 예시: 점수 자료
75, 82, 78, 91, 86, 79, 83, 95, 71, 88

줄기 | 잎
  7  | 1 5 8 9
  8  | 2 3 6 8
  9  | 1 5

📌 줄기잎 그림으로 알 수 있는 것
• 최솟값: 가장 위 줄기의 가장 작은 잎 → 71
• 최댓값: 가장 아래 줄기의 가장 큰 잎 → 95
• 중앙값: 자료를 크기순으로 나열했을 때 가운데 값
• 분포의 모양(몰려 있는 구간)

⚠️ 주의
• 잎의 정렬 순서: 반드시 오름차순
• 같은 값이 여러 개면 모두 표시
• 자료 수 = 모든 잎의 개수 합계`,
    keyPoints: [
      '줄기=십의 자리, 잎=일의 자리 (오름차순 정렬)',
      '원자료가 보존됨 → 최솟값·최댓값·중앙값 파악 가능',
      '자료 수 = 전체 잎의 수',
      '등 줄기잎 그림: 두 집단을 한 줄기로 양쪽 비교',
    ],
    examples: [
      {
        question: '다음 자료를 줄기잎 그림으로 나타내시오.\n63, 71, 58, 65, 74, 59, 68, 72, 61, 77',
        answer: '줄기 5: 8 9\n줄기 6: 1 3 5 8\n줄기 7: 1 2 4 7',
        explanation: '50대: 58, 59 → 잎 8, 9\n60대: 61, 63, 65, 68 → 잎 1, 3, 5, 8\n70대: 71, 72, 74, 77 → 잎 1, 2, 4, 7\n총 10개의 잎 = 자료 10개 ✓',
      },
      {
        question: '위 줄기잎 그림에서 최솟값, 최댓값, 중앙값을 구하시오.',
        answer: '최솟값=58, 최댓값=77, 중앙값=66.5',
        explanation: '최솟값: 줄기5, 잎8 → 58\n최댓값: 줄기7, 잎7 → 77\n중앙값: 10개 자료 → 5번째(65)·6번째(68) 평균\n= (65+68)÷2 = 66.5',
      },
    ],
    practice: {
      question: '10명 학생의 국어 점수가 다음과 같다.\n82, 75, 91, 68, 84, 77, 93, 71, 88, 65\n줄기잎 그림을 완성하고, 중앙값을 구하시오.',
      answer: '줄기6: 5 8 / 줄기7: 1 5 7 / 줄기8: 2 4 8 / 줄기9: 1 3\n중앙값 = (77+82)÷2 = 79.5',
      hint: '자료를 크기순 나열: 65,68,71,75,77,82,84,88,91,93\n중앙값: 10개 → 5번째(77)와 6번째(82)의 평균',
      steps: [
        '자료 크기순: 65, 68, 71, 75, 77, 82, 84, 88, 91, 93',
        '줄기 6: 잎 5, 8',
        '줄기 7: 잎 1, 5, 7',
        '줄기 8: 잎 2, 4, 8',
        '줄기 9: 잎 1, 3',
        '중앙값: (77+82)÷2 = 79.5',
      ],
    },
    misconceptions: ['ST02'],
  },
  {
    id: 'SA03',
    title: '도수분포표',
    subtitle: '자료를 계급으로 나누어 도수분포표를 만들고 해석한다',
    icon: '📊',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    textColor: 'text-blue-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['도수분포표', '계급', '도수', '계급값', '계급크기'],
    objective: '도수분포표의 계급·도수·계급값의 의미를 이해하고, 자료를 도수분포표로 나타낼 수 있다.',
    concept: `**도수분포표 (Frequency Distribution Table)**

📌 용어 정리
• 계급(Class): 자료를 나누는 구간 (예: 10분 이상 20분 미만)
• 계급의 크기: 계급의 너비 (위 예: 10분)
• 계급값(Class Mark): 계급의 중간값 = (계급 하한+계급 상한)÷2
• 도수(Frequency): 각 계급에 속하는 자료의 수
• 도수의 합 = 전체 자료의 수

📌 예시: 통학 시간 도수분포표
| 계급(분) | 계급값 | 도수(명) |
|----------|--------|---------|
| 10 이상 20 미만 | 15 | 5 |
| 20 이상 30 미만 | 25 | 12 |
| 30 이상 40 미만 | 35 | 8 |
| 40 이상 50 미만 | 45 | 3 |
| 합계 | | 28 |

📌 만드는 방법
① 자료의 최솟값과 최댓값 파악
② 계급의 수(보통 5~15개)와 계급 크기 결정
③ 각 계급에 속하는 자료의 수(도수) 세기
④ 표 완성

⚠️ 주의
• 계급의 크기는 모두 같아야 함
• 경계값은 상한에 포함 안 됨 (이상~미만)
• 원자료는 도수분포표에서 복원 불가
• 계급값으로 평균 추정: 평균 ≈ Σ(계급값×도수)÷전체도수`,
    keyPoints: [
      '계급값 = (하한+상한)÷2 (계급의 중간값)',
      '계급 크기는 모두 동일해야 함',
      '도수의 합 = 전체 자료의 수',
      '경계값은 상한에 미포함 (이상~미만 표기)',
    ],
    examples: [
      {
        question: '계급 "20 이상 30 미만"의 계급값은?\n이 계급의 도수가 8이고 전체 도수가 40이라면\n이 계급의 비율(상대도수)은?',
        answer: '계급값=25, 상대도수=0.2 (20%)',
        explanation: '계급값 = (20+30)÷2 = 25\n상대도수 = 8÷40 = 0.2 (=20%)',
      },
      {
        question: '어느 반 학생 25명의 운동 시간 도수분포표에서\n계급 "40분~50분"의 도수가 5, "50분~60분"의 도수가 3이다.\n두 계급의 계급값의 합과 도수의 합을 구하시오.',
        answer: '계급값의 합=100, 도수의 합=8',
        explanation: '40~50분: 계급값=(40+50)÷2=45\n50~60분: 계급값=(50+60)÷2=55\n계급값 합=45+55=100\n도수 합=5+3=8',
      },
    ],
    practice: {
      question: '다음 자료는 20명 학생의 수면 시간(시간)이다.\n6,7,8,5,7,9,6,8,7,6,8,9,7,5,6,8,7,9,6,7\n계급 크기를 1로 하여 5이상~10미만 구간의\n도수분포표를 완성하고 도수가 가장 큰 계급을 구하시오.',
      answer: '5시간: 2명, 6시간: 5명, 7시간: 6명, 8시간: 4명, 9시간: 3명\n도수 최대: 7이상 8미만 (6명)',
      hint: '각 시간대별 개수를 세어보세요!\n5: 5,5 → 2개 / 6: 6,6,6,6,6 → 5개 / 7: 7,7,7,7,7,7 → 6개',
      steps: [
        '5 이상 6 미만: 5, 5 → 도수=2',
        '6 이상 7 미만: 6, 6, 6, 6, 6 → 도수=5',
        '7 이상 8 미만: 7, 7, 7, 7, 7, 7 → 도수=6 (최대)',
        '8 이상 9 미만: 8, 8, 8, 8 → 도수=4',
        '9 이상 10 미만: 9, 9, 9 → 도수=3',
        '합계: 2+5+6+4+3=20 ✓',
      ],
    },
    misconceptions: ['ST03'],
  },
  {
    id: 'SA04',
    title: '히스토그램과 도수분포다각형',
    subtitle: '도수분포를 히스토그램과 다각형 그래프로 나타내고 해석한다',
    icon: '📈',
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
    textColor: 'text-orange-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['히스토그램', '도수분포다각형', '막대그래프', '넓이'],
    objective: '도수분포표를 히스토그램과 도수분포다각형으로 나타내고, 그래프를 해석할 수 있다.',
    concept: `**히스토그램과 도수분포다각형**

📌 히스토그램 (Histogram)
• 가로축: 계급(구간), 세로축: 도수
• 막대 사이에 간격이 없음 (연속 자료)
• 일반 막대그래프와 차이: 간격 없음!
• 각 막대의 넓이 = 계급 크기 × 도수

📌 도수분포다각형
• 히스토그램 각 막대 윗변의 중점(계급값, 도수)을 선으로 연결
• 양 끝: 도수 0인 가상 계급까지 연결 (꺾은선이 x축과 만남)
• 두 집단의 분포 비교에 편리!

📌 히스토그램 ↔ 도수분포다각형
• 히스토그램의 전체 넓이 = 도수분포다각형이 x축과 이루는 넓이
• 두 값 모두 = 계급 크기 × 전체 도수

📌 그래프 해석
• 막대가 높을수록 → 그 계급의 도수가 큼
• 분포가 오른쪽으로 치우침 → 큰 값에 자료 집중
• 분포가 왼쪽으로 치우침 → 작은 값에 자료 집중
• 종 모양(bell shape) → 가운데 집중

⚠️ 주의
• 히스토그램은 막대 사이 간격이 없어야 함
• 도수분포다각형은 양 끝을 반드시 x축에 연결
• 계급 수가 다른 자료는 상대도수로 비교`,
    keyPoints: [
      '히스토그램: 막대 사이 간격 없음 (연속형 자료)',
      '도수분포다각형: 각 막대 윗변 중점 연결 + 양 끝 x축 연결',
      '히스토그램 넓이 = 계급 크기 × 전체 도수',
      '두 집단 비교 시 도수분포다각형이 편리',
    ],
    examples: [
      {
        question: '계급 크기가 5이고 전체 도수가 30인 히스토그램에서\n히스토그램의 전체 넓이를 구하시오.',
        answer: '150',
        explanation: '전체 넓이 = 계급 크기 × 전체 도수\n= 5 × 30 = 150',
      },
      {
        question: '도수분포다각형을 그릴 때\n양 끝을 x축에 연결하는 이유는 무엇인가?',
        answer: '다각형이 x축과 이루는 넓이가 히스토그램 전체 넓이와 같아지도록 하기 위해',
        explanation: '양 끝에 도수 0인 가상 계급을 추가해 연결하면\n다각형이 감싸는 넓이 = 히스토그램 전체 넓이\n이를 통해 두 그래프의 일관성이 유지됨',
      },
    ],
    practice: {
      question: '계급 크기가 10인 도수분포표에서\n도수가 차례로 3, 8, 12, 5, 2일 때\n① 전체 도수를 구하시오.\n② 히스토그램의 전체 넓이를 구하시오.\n③ 도수가 가장 큰 계급의 막대 넓이를 구하시오.',
      answer: '① 30  ② 300  ③ 120',
      hint: '① 도수 합계. ② 계급크기×전체도수. ③ 계급크기×최대도수(12)',
      steps: [
        '① 전체 도수 = 3+8+12+5+2 = 30',
        '② 히스토그램 넓이 = 10 × 30 = 300',
        '③ 도수 최대 = 12',
        '   최대 막대 넓이 = 10 × 12 = 120',
      ],
    },
    misconceptions: ['ST04'],
  },
  {
    id: 'SA05',
    title: '상대도수와 누적도수',
    subtitle: '상대도수와 누적도수를 구하고, 서로 다른 크기의 집단을 비교한다',
    icon: '⚖️',
    color: 'bg-rose-500',
    lightColor: 'bg-rose-50',
    borderColor: 'border-rose-300',
    textColor: 'text-rose-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['상대도수', '누적도수', '누적상대도수', '비교'],
    objective: '상대도수와 누적도수를 구하고, 크기가 다른 두 집단의 분포를 상대도수로 비교할 수 있다.',
    concept: `**상대도수와 누적도수**

📌 상대도수 (Relative Frequency)
• 상대도수 = 그 계급의 도수 ÷ 전체 도수
• 범위: 0 이상 1 이하
• 전체 상대도수의 합 = 1
• 백분율(%) = 상대도수 × 100

📌 상대도수를 쓰는 이유
• 전체 자료의 수(n)가 다른 두 집단 비교 가능!
• 도수가 달라도 상대도수로 공정하게 비교

📌 누적도수 (Cumulative Frequency)
• 처음 계급부터 그 계급까지의 도수를 모두 더한 값
• 마지막 계급의 누적도수 = 전체 도수

📌 누적상대도수
• 처음 계급부터 그 계급까지의 상대도수 합
• 마지막 계급의 누적상대도수 = 1

📌 예시표
| 계급 | 도수 | 상대도수 | 누적도수 | 누적상대도수 |
|------|------|---------|---------|------------|
| 10~20 | 4 | 0.2 | 4 | 0.2 |
| 20~30 | 8 | 0.4 | 12 | 0.6 |
| 30~40 | 6 | 0.3 | 18 | 0.9 |
| 40~50 | 2 | 0.1 | 20 | 1.0 |
| 합계 | 20 | 1.0 | - | - |

⚠️ 주의
• 상대도수의 합은 반드시 1 (반올림 오차 주의)
• 누적도수: 계속 증가 → 마지막=전체 도수
• 집단 비교 시 반드시 상대도수 사용 (도수 사용 불가)`,
    keyPoints: [
      '상대도수 = 도수 ÷ 전체도수 (합=1)',
      '누적도수 = 처음부터 해당 계급까지 도수의 합',
      '크기 다른 집단 비교 → 반드시 상대도수 사용',
      '누적상대도수의 마지막 값 = 1',
    ],
    examples: [
      {
        question: '전체 40명인 집단에서 어느 계급의 도수가 10이다.\n이 계급의 상대도수와 백분율을 구하시오.',
        answer: '상대도수=0.25, 백분율=25%',
        explanation: '상대도수 = 10÷40 = 0.25\n백분율 = 0.25×100 = 25%',
      },
      {
        question: '1반(30명)과 2반(50명)에서\n80점 이상인 학생이 각각 9명, 15명이다.\n어느 반의 성취도가 더 높은가?',
        answer: '1반이 더 높다 (30% > 30%... 동일, 재확인 필요)',
        explanation: '1반 상대도수 = 9÷30 = 0.30 (30%)\n2반 상대도수 = 15÷50 = 0.30 (30%)\n두 반의 80점 이상 비율이 같다!\n→ 도수(9 vs 15)로 비교하면 오류 발생',
      },
    ],
    practice: {
      question: '어느 반 학생 25명의 점수 도수분포표에서\n도수가 차례로 2, 5, 10, 6, 2이다.\n① 각 계급의 상대도수를 구하시오.\n② 3번째 계급까지의 누적도수와 누적상대도수를 구하시오.',
      answer: '① 0.08, 0.20, 0.40, 0.24, 0.08\n② 누적도수=17, 누적상대도수=0.68',
      hint: '상대도수=도수÷25. 누적도수=2+5+10=17. 누적상대도수=0.08+0.20+0.40=0.68',
      steps: [
        '① 상대도수: 2÷25=0.08, 5÷25=0.20, 10÷25=0.40, 6÷25=0.24, 2÷25=0.08',
        '   합계: 0.08+0.20+0.40+0.24+0.08=1.00 ✓',
        '② 3번째 계급까지 누적도수: 2+5+10=17',
        '   누적상대도수: 0.08+0.20+0.40=0.68',
      ],
    },
    misconceptions: ['ST05'],
  },
]

// ── 하위 컴포넌트 ────────────────────────────────────────────────────────────
function PracticePanel({ practice }: { practice: typeof STEPS[0]['practice'] }) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  return (
    <div className="mt-4 bg-violet-50 border border-violet-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-violet-600 mb-2 flex items-center gap-1">
        <PenTool size={12} /> 연습 문제
      </p>
      <p className="text-sm text-slate-800 font-medium mb-3 whitespace-pre-line">{practice.question}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold transition-colors">
          💡 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold transition-colors">
          📋 풀이 단계
        </button>
        <button onClick={() => setShowAnswer(!showAnswer)}
          className="text-xs px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200 font-semibold transition-colors">
          ✅ 정답 확인
        </button>
      </div>
      {showHint && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 {practice.hint}
        </div>
      )}
      {showSteps && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-bold text-blue-700 mb-2">단계별 풀이:</p>
          {practice.steps.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs text-blue-800 mb-1">
              <span className="font-bold text-blue-500 w-4">{i + 1}.</span>
              <span className="font-mono">{s}</span>
            </div>
          ))}
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 p-3 bg-violet-50 border border-violet-200 rounded-xl text-xs text-violet-800 font-mono whitespace-pre-line">
          {practice.answer}
        </div>
      )}
    </div>
  )
}

function ExampleCard({ example, idx }: { example: typeof STEPS[0]['examples'][0], idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-sm font-semibold text-slate-700">예제 {idx + 1}: {example.question.slice(0, 28)}...</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-800 whitespace-pre-line">{example.question}</p>
          <div className="bg-violet-50 rounded-lg p-3">
            <p className="text-xs font-bold text-violet-600 mb-1">풀이</p>
            <p className="text-sm font-mono text-violet-800 whitespace-pre-line">{example.answer}</p>
          </div>
          <p className="text-xs text-slate-500 italic whitespace-pre-line">{example.explanation}</p>
        </div>
      )}
    </div>
  )
}

function StoryCard({ story }: { story: StepStory }) {
  const [lineIdx, setLineIdx] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const speakerColor: Record<string, string> = {
    student: 'bg-violet-100 text-violet-800',
    teacher: 'bg-blue-100 text-blue-800',
    friend: 'bg-emerald-100 text-emerald-800',
    narrator: 'bg-amber-100 text-amber-800',
  }
  return (
    <div className="bg-gradient-to-b from-violet-50 to-white border border-violet-200 rounded-2xl p-4">
      <p className="text-xs text-slate-500 italic mb-3">{story.scene}</p>
      <div className="min-h-[80px] mb-3">
        {story.lines.slice(0, lineIdx + 1).map((line, i) => (
          <div key={i} className={`flex gap-2 mb-2 ${i < lineIdx ? 'opacity-50' : ''}`}>
            <span className="text-lg flex-shrink-0">{line.emoji}</span>
            <div>
              {line.name && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-1 ${speakerColor[line.speaker]}`}>
                  {line.name}
                </span>
              )}
              <span className="text-sm text-slate-700">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1 bg-violet-100 rounded-full">
          <div className="h-1 bg-violet-400 rounded-full transition-all"
            style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-400">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      {!revealed ? (
        <div className="flex gap-2">
          {lineIdx < story.lines.length - 1 && (
            <button onClick={() => setLineIdx(lineIdx + 1)}
              className="flex-1 py-2 bg-violet-100 hover:bg-violet-200 text-violet-700 font-semibold rounded-xl text-xs transition-colors">
              다음 대화 →
            </button>
          )}
          {lineIdx === story.lines.length - 1 && (
            <button onClick={() => setRevealed(true)}
              className="flex-1 py-2 bg-violet-500 hover:bg-violet-600 text-white font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-1">
              <Sparkles size={12} /> 핵심 공식 보기
            </button>
          )}
        </div>
      ) : (
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
          <p className="text-lg text-center mb-1">{story.revealEmoji}</p>
          <p className="text-xs text-violet-800 font-mono text-center">{story.reveal}</p>
        </div>
      )}
    </div>
  )
}

function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const visible = fragments.filter(f => !dismissed.has(f.fragment.id)).slice(0, 3)
  if (visible.length === 0) return null
  return (
    <div className="mb-4 card border border-amber-200 bg-amber-50">
      <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
        <Brain size={12} /> AI 예상 취약 개념
      </p>
      <div className="space-y-1.5">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
              <span>{f.fragment.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold truncate ${cfg.color}`}>{f.fragment.title}</p>
                <p className="text-xs text-slate-500 truncate">{f.fragment.fixTip.slice(0, 40)}...</p>
              </div>
              <button onClick={() => setDismissed(s => new Set([...s, f.fragment.id]))}
                className="text-slate-300 hover:text-slate-500 flex-shrink-0"><X size={11} /></button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
const TOTAL_STEPS = STEPS.length

interface S1_1LearnPageProps { defaultStepId?: string | null }

export default function S1_1LearnPage({ defaultStepId }: S1_1LearnPageProps) {
  const [selectedStep, setSelectedStep] = useState<typeof STEPS[0] | null>(null)
  const [activeTab, setActiveTab] = useState<'story' | 'concept' | 'examples' | 'practice'>('story')

  const { completeStep } = useProgressStore(s => ({ completeStep: s.completeStep }))
  const learnProgress = useLearnProgress(UNIT_ID)
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)

  const completedCount = learnProgress.completedCount
  const progressPct = learnProgress.pct

  useEffect(() => {
    if (defaultStepId) {
      const step = STEPS.find(s => s.id === defaultStepId)
      if (step) { setSelectedStep(step); setActiveTab('concept') }
    }
  }, [defaultStepId])

  const handleComplete = (step: typeof STEPS[0]) => {
    completeStep(UNIT_ID, step.id, step.title)
    setSelectedStep(null)
  }

  if (!selectedStep) {
    return (
      <div className="p-4 space-y-4">
        <PredictedFragmentPanel fragments={predictedFragments} />

        <div className="card bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center text-2xl">📊</div>
            <div>
              <h2 className="text-lg font-black text-slate-800">자료의 정리와 해석</h2>
              <p className="text-xs text-slate-500">S1-1 · 통계 영역</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-violet-100 rounded-full overflow-hidden">
              <div className="h-2 bg-violet-500 rounded-full transition-all"
                style={{ width: `${progressPct}%` }} />
            </div>
            <span className="text-xs font-bold text-violet-600 w-16 text-right">
              {completedCount}/{TOTAL_STEPS} 완료
            </span>
          </div>
        </div>

        <div className="space-y-2">
          {STEPS.map((step) => {
            const stepProg = learnProgress.steps[step.id]
            const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
            const video = getApprovedByTag(step.id)
            return (
              <div key={step.id}
                className={`card border-2 cursor-pointer transition-all hover:shadow-md ${done ? 'border-violet-200 bg-violet-50/30' : 'border-slate-200 hover:border-violet-300'}`}
                onClick={() => { setSelectedStep(step); setActiveTab('story') }}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${done ? 'bg-violet-100' : step.lightColor}`}>
                    {done ? '✅' : step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-800">{step.title}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${step.difficultyColor}`}>
                        {step.difficulty}
                      </span>
                      {video && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold flex items-center gap-0.5">
                          <Youtube size={9} /> 영상
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{step.subtitle}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-slate-400">{step.id}</span>
                    <ChevronRight size={15} className="text-slate-300" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {completedCount === TOTAL_STEPS && (
          <div className="card bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-300 text-center">
            <div className="text-3xl mb-2">🎉</div>
            <p className="text-sm font-bold text-violet-700">모든 개념 학습 완료!</p>
            <p className="text-xs text-slate-500 mt-1">실전 TEST에 도전해보세요</p>
          </div>
        )}
      </div>
    )
  }

  const step = selectedStep
  const stepProg = learnProgress.steps[step.id]
  const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
  const story = STEP_STORIES[step.id]
  const video = getApprovedByTag(step.id)

  const tabs = [
    { id: 'story' as const, label: '스토리', icon: <MessageCircle size={13} /> },
    { id: 'concept' as const, label: '개념', icon: <BookOpen size={13} /> },
    { id: 'examples' as const, label: '예제', icon: <FileText size={13} /> },
    { id: 'practice' as const, label: '연습', icon: <PenTool size={13} /> },
  ]

  return (
    <div className="flex flex-col h-full">
      <div className={`${step.lightColor} border-b ${step.borderColor} px-4 pt-3 pb-0 flex-shrink-0`}>
        <button onClick={() => setSelectedStep(null)}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-2 transition-colors">
          ← 목록으로
        </button>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">{step.icon}</span>
          <div>
            <h2 className="text-base font-black text-slate-800">{step.title}</h2>
            <p className="text-xs text-slate-500">{step.subtitle}</p>
          </div>
          {done && <CheckCircle size={18} className="text-emerald-500 ml-auto" />}
        </div>
        <div className="flex gap-1 flex-wrap mb-2">
          {step.tags.map(tag => (
            <span key={tag} className="text-xs px-2 py-0.5 bg-white/80 border border-violet-200 text-violet-700 rounded-full">
              #{tag}
            </span>
          ))}
        </div>
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1 px-3 py-2 rounded-t-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                activeTab === t.id
                  ? 'bg-white text-violet-700 border-t border-l border-r border-violet-200'
                  : 'text-slate-500 hover:text-slate-700'
              }`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === 'story' && story && <StoryCard story={story} />}

        {activeTab === 'concept' && (
          <div className="space-y-4">
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
              <p className="text-xs font-bold text-violet-700 mb-1 flex items-center gap-1">
                <Target size={11} /> 학습 목표
              </p>
              <p className="text-sm text-slate-700">{step.objective}</p>
            </div>
            <div className="card">
              <p className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1">
                <BookOpen size={11} /> 핵심 개념
              </p>
              <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                {step.concept}
              </div>
            </div>
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
              <p className="text-xs font-bold text-violet-700 mb-2 flex items-center gap-1">
                <Star size={11} /> 핵심 포인트
              </p>
              <ul className="space-y-1.5">
                {step.keyPoints.map((pt, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                    <span className="text-violet-500 font-bold flex-shrink-0">✓</span>
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
            {video && (
              <button onClick={() => setVideoModal({ stepId: step.id, label: step.title })}
                className="w-full flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors">
                <Youtube size={16} className="text-red-600" />
                <span className="text-sm font-semibold text-red-700">개념 설명 영상 보기</span>
                <ArrowRight size={13} className="text-red-400 ml-auto" />
              </button>
            )}
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="space-y-3">
            <p className="text-xs font-bold text-slate-500">📝 예제 문제</p>
            {step.examples.map((ex, i) => (
              <ExampleCard key={i} example={ex} idx={i} />
            ))}
          </div>
        )}

        {activeTab === 'practice' && (
          <PracticePanel practice={step.practice} />
        )}

        {!done ? (
          <button onClick={() => handleComplete(step)}
            className="w-full py-3 bg-violet-500 hover:bg-violet-600 text-white font-black rounded-2xl transition-colors flex items-center justify-center gap-2">
            <CheckCircle size={16} /> 학습 완료
          </button>
        ) : (
          <div className="flex items-center justify-center gap-2 py-3 bg-violet-50 border border-violet-200 rounded-2xl">
            <CheckCircle size={16} className="text-violet-500" />
            <span className="text-sm font-bold text-violet-700">완료된 단계입니다</span>
          </div>
        )}
      </div>

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
